// src/components/SuperAdmin/SuperAdminHeader.js
import React, { useState, useMemo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';

/**
 * Props:
 *  - userName: string (passed from ProtectedLayout)
 *  - handleLogout: function (passed from ProtectedLayout)
 *
 * Matches SuperAdminRoutes: /dashboard, /blog-dashboard, /case-studies-dashboard, /brand-details
 */
export default function SuperAdminHeader({ userName = 'Admin', handleLogout = () => { } }) {
    const [mobileOpen, setMobileOpen] = useState(false);

    const initials = useMemo(() => {
        if (!userName) return 'A';
        const parts = userName.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }, [userName]);

    const toggleMobile = useCallback(() => setMobileOpen((s) => !s), []);

    const navLinks = [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/blog-dashboard', label: 'Blog' },
        { to: '/case-studies-dashboard', label: 'Case Studies' },
        { to: '/brand-details', label: 'Brand' },
    ];

    const linkClass = ({ isActive }) =>
        (isActive ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-700 hover:text-indigo-600') +
        ' px-2 py-1';

    return (
        <header className="bg-white shadow">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Left: title + avatar */}
                    <div className="flex items-center space-x-4">
                        <div className="text-lg font-semibold">Admin Panel</div>
                        <div className="hidden sm:flex items-center space-x-2">
                            <div
                                aria-hidden
                                className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-medium"
                                title={userName}
                            >
                                {initials}
                            </div>
                            <div className="text-sm text-gray-700 hidden md:block">Hi, <span className="font-medium">{userName}</span></div>
                        </div>
                    </div>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex md:items-center md:space-x-6" aria-label="Primary">
                        {navLinks.map((link) => (
                            <NavLink key={link.to} to={link.to} className={linkClass}>
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Actions + Mobile menu button */}
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={handleLogout}
                            title="Logout"
                            aria-label="Logout"
                            className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-200"
                        >
                            <LogOut size={16} />
                            <span className="text-sm">Logout</span>
                        </button>

                        {/* Mobile menu button */}
                        <button
                            onClick={toggleMobile}
                            aria-expanded={mobileOpen}
                            aria-label="Toggle menu"
                            className="inline-flex items-center justify-center p-2 rounded-md md:hidden focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        >
                            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile nav panel */}
            {mobileOpen && (
                <div className="md:hidden border-t border-gray-100 bg-white">
                    <div className="px-4 py-3 space-y-1">
                        {navLinks.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                onClick={() => setMobileOpen(false)}
                                className={linkClass}
                            >
                                {link.label}
                            </NavLink>
                        ))}

                        <button
                            onClick={() => {
                                setMobileOpen(false);
                                handleLogout();
                            }}
                            className="w-full text-left px-2 py-2 rounded-md text-red-700 bg-red-50 hover:bg-red-100 flex items-center gap-2"
                        >
                            <LogOut size={16} />
                            Logout
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
